import fpgConfiguration from '@/store/fpgConfiguration';
import { removeLeadingSlash } from '@/utils';
import configuration from '@/store/configuration';
import AuthService from './services/AuthService';
import AccountStaticService from '@/services/AccountStaticService';

function getModifiedPath(path) {
  if (typeof path === 'string') {
    return removeLeadingSlash(path);
  }
  return '';
}

function prependFleetAccount(path) {
  const modifiedPath = getModifiedPath(path);
  const prefixedAndModifiedPath =
    fpgConfiguration &&
    fpgConfiguration.state &&
    fpgConfiguration.state.isTileMode
      ? `fleet-account/${modifiedPath}`
      : modifiedPath;
  return '/' + prefixedAndModifiedPath;
}

export function getStaticAssetsBaseUrl() {
  return configuration.state.accountStaticBaseUrl;
}

function constructUrlIfValidAbsoluteUrl(urlString) {
  try {
    return new URL(urlString);
  } catch (e) {
    return null;
  }
}

function isFleetAccountUrl(urlString) {
  return urlString.includes(configuration.state.fleetAccountHost);
}

export async function routeToApp(appId, appUrl, countryCode) {
  if (isFleetAccountUrl(appUrl)) {
    AuthService.getPartnerTokenToRedirect(appId, countryCode)
      .then((res) => {
        window.open(res.data, '_blank');
      })
      .catch((error) => {
        console.log(error);
      });
  } else {
    window.open(appUrl, '_blank');
  }
}

export function getProxiedApplicationUrl(urlString, countryCode) {
  let url = constructUrlIfValidAbsoluteUrl(urlString);
  let currentlyOnFordProDomainProxyingToFleetAccountUrl =
    fpgConfiguration.state.isTileMode &&
    url?.host === configuration.state.fleetAccountHost;

  if (currentlyOnFordProDomainProxyingToFleetAccountUrl) {
    url.host = new URL(getFordProDomain(countryCode)).host;
    url.pathname = prependFleetAccount(url.pathname);
    return url.toString();
  }

  return replaceFordProUrlDomainBasedOnCountry(urlString, countryCode);
}

export function getFordProDomain(countryCode, fullUrl = true) {
  const domain = configuration.state.fordProDomains[countryCode];
  let fordProDomain;
  if (domain !== undefined) {
    fordProDomain = domain;
  } else {
    fordProDomain = configuration.state.fordProDomains['DEFAULT'];
  }

  if (fullUrl) {
    return configuration.state.fordProUrlSchemeAndSubDomain + fordProDomain;
  }
  return fordProDomain;
}

export function replaceFordProUrlDomainBasedOnCountry(url, countryCode) {
  if (typeof url !== 'string') {
    return url;
  }
  const defaultDomain = configuration.state.fordProDomains['DEFAULT'];
  const domain = getFordProDomain(countryCode, false);
  if (domain !== defaultDomain) {
    return url.split(defaultDomain).join(domain);
  }
  return url;
}

// The /pro/auth/v1/partner-app response is a login URL whose own `redirect`
// query param is hardcoded to the bare domain, discarding the caller's
// state.redirect. Rewrite that inner target's path/query to the desired Ford
// Pro deep link so the user lands on the intended page after MFA login.
// Uses the URL API so both path formats (with and without a query string) are
// handled correctly and no stray `?` is left behind. Falls back to returning
// the input unchanged if the response has no redirect param or can't be parsed.
export function buildFordProLoginRedirect(partnerAppUrl, desiredRelativePath) {
  try {
    const loginUrl = new URL(partnerAppUrl);
    const currentTarget = loginUrl.searchParams.get('redirect');
    if (!currentTarget) {
      return partnerAppUrl;
    }

    const targetUrl = new URL(currentTarget);
    const desired = new URL(desiredRelativePath, 'https://placeholder.invalid');
    targetUrl.pathname = desired.pathname;
    targetUrl.search = desired.search;

    loginUrl.searchParams.set('redirect', targetUrl.toString());
    return loginUrl.toString();
  } catch (e) {
    return partnerAppUrl;
  }
}

export async function verifyStaticAssetUrl(pdfUrl) {
  return AccountStaticService.get(pdfUrl)
    .then(() => getStaticAssetsBaseUrl() + pdfUrl)
    .catch((err) => {
      console.log(err);
      return '';
    });
}

const LEGACY_TO_FORD_PRO_PATH_MAP = {
  '/': '/dashboard/',
  '/vehicles': '/fleet-admin/v2/?page=vehicles&tab=my-fleet',
  '/equipment': '/fleet-admin/v2/?page=equipment&tab=my-equipment',
  '/users': '/fleet-admin/users/v2/',
  '/groups': '/fleet-admin/v2/?page=groups',
  // /billing and /settings have no Ford Pro equivalent — fall through to dashboard
};

// Ford Pro's Unified UI path-based admin routing — live on stage now,
// prod follows once Ford Pro rolls it out there (~12 Aug 2026). Gated by
// fpgUnifiedAdminRoutingEnabled so prod keeps using the query-param
// routes above until then.
const UNIFIED_ADMIN_ROUTE_OVERRIDES = {
  '/vehicles': '/admin/vehicles/?tab=my-fleet',
  '/equipment': '/admin/equipment/?tab=my-equipment',
  '/groups': '/admin/groups/',
};

export function getFordProRedirectPath(routePath) {
  if (typeof routePath !== 'string') {
    return '/dashboard/';
  }
  const map = configuration.state.fpgUnifiedAdminRoutingEnabled
    ? { ...LEGACY_TO_FORD_PRO_PATH_MAP, ...UNIFIED_ADMIN_ROUTE_OVERRIDES }
    : LEGACY_TO_FORD_PRO_PATH_MAP;
  const topLevelSegment = '/' + routePath.replace(/^\/+/, '').split('/')[0];
  return map[topLevelSegment] ?? '/dashboard/';
}
